import { useState, useEffect, useRef } from 'react';
import Layout from '@/components/Layout';
import { Card, CardHeader, CardTitle, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Label } from '@/components/ui/label';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '@/components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Badge } from '@/components/ui/badge';
import { Avatar, AvatarFallback } from '@/components/ui/avatar';
import { 
  MessageSquare, Send, Mail, Phone, Users, UserPlus, 
  Mic, Square, Play, Trash2, Clock, CheckCircle, 
  AlertCircle, Search, Filter, RefreshCw
} from 'lucide-react';
import axios from 'axios';
import { toast } from 'sonner';
import { format } from 'date-fns';
import usePermissions from '@/hooks/usePermissions';
import { ReadOnlyBanner, NoAccessScreen } from '@/components/PermissionUI';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export default function CommunicationPage({ user, onLogout, permissions }) {
  const { canEdit, canView, isReadOnly } = usePermissions(permissions);
  // Team Chat State
  const [messages, setMessages] = useState([]);
  const [newMessage, setNewMessage] = useState('');
  const [isRecordingVoice, setIsRecordingVoice] = useState(false);
  const [voiceBlob, setVoiceBlob] = useState(null);
  const mediaRecorderRef = useRef(null);
  const messagesEndRef = useRef(null);

  // Direct Messages State
  const [coaches, setCoaches] = useState([]);
  const [selectedCoach, setSelectedCoach] = useState(null);
  const [directMessages, setDirectMessages] = useState([]);
  const [newDM, setNewDM] = useState('');

  // Email/SMS State
  const [showComposeDialog, setShowComposeDialog] = useState(false);
  const [composeMode, setComposeMode] = useState('email'); // 'email' | 'sms'
  const [recipients, setRecipients] = useState([]);
  const [emailSubject, setEmailSubject] = useState('');
  const [emailBody, setEmailBody] = useState('');
  const [contacts, setContacts] = useState([]);
  const [messageLogs, setMessageLogs] = useState([]);
  const [isSending, setIsSending] = useState(false);
  const [emailTemplates, setEmailTemplates] = useState([]);
  const [selectedTemplate, setSelectedTemplate] = useState('');
  const [templateValues, setTemplateValues] = useState({});
  const [upcomingEvents, setUpcomingEvents] = useState([]);

  useEffect(() => {
    loadMessages();
    loadCoaches();
    loadContacts();
    loadMessageLogs();
    loadEmailTemplates();
  }, [user.team_id]);

  useEffect(() => {
    scrollToBottom();
  }, [messages]);

  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  };

  const loadMessages = async () => {
    try {
      const response = await axios.get(`${API}/messages?team_id=${user.team_id}`);
      setMessages(response.data || []);
    } catch (error) {
      console.error('Failed to load messages');
    }
  };

  const loadCoaches = async () => {
    try {
      const response = await axios.get(`${API}/teams/${user.team_id}/coaches`);
      // API returns { coaches: [...] }; support raw array too
      const list = Array.isArray(response.data) ? response.data : (response.data?.coaches || []);
      // Filter out current user from coach list (coaches have `id` field)
      const otherCoaches = list.filter(c => (c.user_id || c.id) !== user.id).map(c => ({ ...c, user_id: c.user_id || c.id }));
      setCoaches(otherCoaches);
    } catch (error) {
      console.error('Failed to load coaches');
    }
  };

  const loadContacts = async () => {
    try {
      const response = await axios.get(`${API}/contacts?team_id=${user.team_id}`);
      setContacts(response.data || []);
    } catch (error) {
      console.error('Failed to load contacts');
    }
  };

  const loadMessageLogs = async () => {
    try {
      const response = await axios.get(`${API}/messaging/logs?team_id=${user.team_id}&limit=50`);
      setMessageLogs(response.data || []);
    } catch (error) {
      console.error('Failed to load message logs');
    }
  };

  const loadDirectMessages = async (coachId) => {
    try {
      const response = await axios.get(`${API}/messages/direct?team_id=${user.team_id}&user_id=${user.id}&other_user_id=${coachId}`);
      setDirectMessages(response.data || []);
    } catch (error) {
      console.error('Failed to load direct messages');
    }
  };

  // Team Chat Functions
  const sendMessage = async () => {
    if (!newMessage.trim()) return;

    try {
      await axios.post(`${API}/messages`, {
        team_id: user.team_id,
        sender_id: user.id,
        sender_name: user.name,
        content: newMessage,
        message_type: 'text'
      });
      setNewMessage('');
      loadMessages();
    } catch (error) {
      toast.error('Failed to send message');
    }
  };

  // Voice Recording Functions
  const startVoiceRecording = async () => {
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      mediaRecorderRef.current = new MediaRecorder(stream);
      const chunks = [];

      mediaRecorderRef.current.ondataavailable = (e) => chunks.push(e.data);
      mediaRecorderRef.current.onstop = () => {
        const blob = new Blob(chunks, { type: 'audio/webm' });
        setVoiceBlob(blob);
        stream.getTracks().forEach(track => track.stop());
      };

      mediaRecorderRef.current.start();
      setIsRecordingVoice(true);
      toast.info('Recording...');
    } catch (error) {
      toast.error('Failed to access microphone');
    }
  };

  const stopVoiceRecording = () => {
    if (mediaRecorderRef.current && isRecordingVoice) {
      mediaRecorderRef.current.stop();
      setIsRecordingVoice(false);
      toast.success('Recording stopped');
    }
  };

  const sendVoiceMemo = async () => {
    if (!voiceBlob) return;

    const formData = new FormData();
    formData.append('audio', voiceBlob, 'voice-memo.webm');
    formData.append('team_id', user.team_id);
    formData.append('sender_id', user.id);

    try {
      await axios.post(`${API}/messages/voice`, formData, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });
      toast.success('Voice memo sent!');
      setVoiceBlob(null);
      loadMessages();
    } catch (error) {
      toast.error('Failed to send voice memo');
    }
  };

  // Direct Message Functions
  const selectCoach = (coach) => {
    setSelectedCoach(coach);
    loadDirectMessages(coach.user_id);
  };

  const sendDirectMessage = async () => {
    if (!newDM.trim() || !selectedCoach) return;

    try {
      await axios.post(`${API}/messages/direct`, {
        team_id: user.team_id,
        sender_id: user.id,
        sender_name: user.name,
        recipient_id: selectedCoach.user_id,
        content: newDM
      });
      setNewDM('');
      loadDirectMessages(selectedCoach.user_id);
      toast.success('Message sent!');
    } catch (error) {
      toast.error('Failed to send message');
    }
  };

  // Email/SMS Functions
  const handleSendEmail = async () => {
    if (recipients.length === 0 || !emailSubject.trim() || !emailBody.trim()) {
      toast.error('Please fill in all fields');
      return;
    }

    setIsSending(true);
    try {
      await axios.post(`${API}/messaging/send-email`, {
        team_id: user.team_id,
        recipients: recipients,
        subject: emailSubject,
        body: emailBody,
        sender_name: user.name
      });
      toast.success('Email sent successfully!');
      setShowComposeDialog(false);
      resetComposeForm();
      loadMessageLogs();
    } catch (error) {
      toast.error('Failed to send email. Make sure email integration is configured.');
    } finally {
      setIsSending(false);
    }
  };

  const handleSendSMS = async () => {
    if (recipients.length === 0 || !emailBody.trim()) {
      toast.error('Please select recipients and enter a message');
      return;
    }

    setIsSending(true);
    try {
      const response = await axios.post(`${API}/messaging/sms/bulk`, {
        team_id: user.team_id,
        recipient_ids: recipients,
        message: emailBody
      });
      const { sent_count, failed_count, total_recipients } = response.data;
      if (failed_count > 0) {
        toast.warning(`SMS sent to ${sent_count}/${total_recipients} — ${failed_count} failed`);
      } else {
        toast.success(`SMS sent to all ${sent_count} recipients!`);
      }
      setShowComposeDialog(false);
      resetComposeForm();
      loadMessageLogs();
    } catch (error) {
      toast.error('Failed to send SMS. Make sure Quo integration is configured.');
    } finally {
      setIsSending(false);
    }
  };

  const resetComposeForm = () => {
    setRecipients([]);
    setEmailSubject('');
    setEmailBody('');
    setSelectedTemplate('');
    setTemplateValues({});
  };

  const loadEmailTemplates = async () => {
    try {
      const response = await axios.get(`${API}/email-templates`);
      setEmailTemplates(response.data || []);
    } catch (error) {
      console.error('Failed to load email templates');
    }
    try {
      const eventsResponse = await axios.get(`${API}/schedules/upcoming?team_id=${user.team_id}&limit=10`);
      setUpcomingEvents(eventsResponse.data || []);
    } catch (error) {
      console.error('Failed to load upcoming events');
    }
  };

  const applyScheduleEvent = (eventId) => {
    const ev = upcomingEvents.find((e) => e.id === eventId);
    const template = emailTemplates.find((t) => t.id === selectedTemplate);
    if (!ev || !template) return;
    const candidate = {
      opponent: ev.opponent || ev.title,
      date: ev.date,
      time: ev.time || 'TBD',
      location: ev.location || 'TBD',
      focus: ev.notes || ev.title,
      event_name: ev.title,
      new_datetime: `${ev.date}${ev.time ? ' ' + ev.time : ''}`
    };
    const vals = { ...templateValues };
    template.fields.forEach((f) => {
      if (candidate[f]) vals[f] = candidate[f];
    });
    setTemplateValues(vals);
    let subject = template.subject;
    let body = template.default_message;
    Object.entries(vals).forEach(([k, v]) => {
      if (v) {
        subject = subject.split(`{${k}}`).join(v);
        body = body.split(`{${k}}`).join(v);
      }
    });
    setEmailSubject(subject);
    setEmailBody(body);
    toast.success(`Filled from: ${ev.title}`);
  };

  const autoGenerateRecap = async () => {
    try {
      const response = await axios.post(`${API}/email-templates/weekly-recap/${user.team_id}`);
      setTemplateValues(response.data.values || {});
      setEmailSubject(response.data.subject);
      setEmailBody(response.data.message);
      toast.success("Weekly recap generated from this week's stats and schedule!");
    } catch (error) {
      toast.error('Failed to generate weekly recap');
    }
  };

  const applyTemplate = (templateId) => {
    setSelectedTemplate(templateId);
    setTemplateValues({});
    const template = emailTemplates.find((t) => t.id === templateId);
    if (template) {
      setEmailSubject(template.subject);
      setEmailBody(template.default_message);
    }
  };

  const updateTemplateValue = (field, value) => {
    const vals = { ...templateValues, [field]: value };
    setTemplateValues(vals);
    const template = emailTemplates.find((t) => t.id === selectedTemplate);
    if (template) {
      let subject = template.subject;
      let body = template.default_message;
      Object.entries(vals).forEach(([k, v]) => {
        if (v) {
          subject = subject.split(`{${k}}`).join(v);
          body = body.split(`{${k}}`).join(v);
        }
      });
      setEmailSubject(subject);
      setEmailBody(body);
    }
  };

  const toggleRecipient = (contact) => {
    const contactId = contact.id;
    if (recipients.includes(contactId)) {
      setRecipients(recipients.filter(r => r !== contactId));
    } else {
      setRecipients([...recipients, contactId]);
    }
  };

  const lastSmsBlast = messageLogs.find((log) => log.type === 'bulk_sms');
  const [isRetryingSms, setIsRetryingSms] = useState(false);

  const handleRetryFailedSms = async () => {
    if (!lastSmsBlast?.id) return;
    setIsRetryingSms(true);
    try {
      const response = await axios.post(`${API}/messaging/sms/bulk/${lastSmsBlast.id}/retry-failed`);
      toast.success(response.data.message);
      loadMessageLogs();
    } catch (error) {
      toast.error('Failed to retry SMS. Make sure Quo integration is configured.');
    } finally {
      setIsRetryingSms(false);
    }
  };

  const selectAllRecipients = () => {
    const allIds = contacts.map(c => c.id);
    setRecipients(allIds);
  };

  return (
    <Layout user={user} onLogout={onLogout}>
      <div className="p-8" data-testid="communication-page">
        {!canView('communication') ? (
          <NoAccessScreen area="Communication" />
        ) : (
        <>
        {isReadOnly('communication') && <ReadOnlyBanner area="Communication" />}
        <div className="border-b border-border pb-6 mb-6">
          <h1 className="font-heading text-5xl font-black uppercase tracking-tighter text-primary">
            Team Communication
          </h1>
          <p className="text-muted-foreground mt-2">Message your team, coaches, and contacts via chat, email, or SMS</p>
        </div>

        <Tabs defaultValue="direct" className="space-y-6">
          <TabsList className="grid grid-cols-3 w-full max-w-2xl">
            <TabsTrigger value="direct" data-testid="tab-direct">
              <Users className="h-4 w-4 mr-2" />
              Direct Messages
            </TabsTrigger>
            <TabsTrigger value="email-sms" data-testid="tab-email-sms">
              <Mail className="h-4 w-4 mr-2" />
              Email / SMS
            </TabsTrigger>
            <TabsTrigger value="history" data-testid="tab-history">
              <Clock className="h-4 w-4 mr-2" />
              History
            </TabsTrigger>
          </TabsList>

          {/* Direct Messages Tab */}
          <TabsContent value="direct">
            <div className="grid grid-cols-3 gap-6 h-[600px]">
              {/* Coaches List */}
              <Card className="border-2">
                <CardHeader className="border-b bg-muted/30">
                  <CardTitle className="font-heading uppercase text-lg">Coaches</CardTitle>
                </CardHeader>
                <CardContent className="p-2 overflow-y-auto">
                  {coaches.length === 0 ? (
                    <p className="text-sm text-muted-foreground p-4 text-center">No other coaches on this team</p>
                  ) : (
                    coaches.map((coach) => (
                      <div
                        key={coach.user_id}
                        className={`flex items-center gap-3 p-3 rounded-lg cursor-pointer hover:bg-muted ${
                          selectedCoach?.user_id === coach.user_id ? 'bg-muted' : ''
                        }`}
                        onClick={() => selectCoach(coach)}
                        data-testid={`coach-${coach.user_id}`}
                      >
                        <Avatar>
                          <AvatarFallback>{coach.name?.charAt(0) || 'C'}</AvatarFallback>
                        </Avatar>
                        <div>
                          <p className="font-semibold text-sm">{coach.name || 'Coach'}</p>
                          <p className="text-xs text-muted-foreground">{coach.role || 'Assistant Coach'}</p>
                        </div>
                      </div>
                    ))
                  )}
                </CardContent>
              </Card>

              {/* Direct Message Thread */}
              <Card className="border-2 col-span-2 flex flex-col">
                <CardHeader className="border-b bg-muted/30">
                  <CardTitle className="font-heading uppercase text-lg">
                    {selectedCoach ? `Chat with ${selectedCoach.name}` : 'Select a coach'}
                  </CardTitle>
                </CardHeader>
                <CardContent className="flex-1 overflow-y-auto p-4 space-y-4">
                  {!selectedCoach ? (
                    <div className="text-center py-12">
                      <Users className="h-16 w-16 mx-auto text-muted-foreground opacity-50" />
                      <p className="text-muted-foreground mt-4">Select a coach to start messaging</p>
                    </div>
                  ) : directMessages.length === 0 ? (
                    <div className="text-center py-12">
                      <MessageSquare className="h-16 w-16 mx-auto text-muted-foreground opacity-50" />
                      <p className="text-muted-foreground mt-4">No messages yet</p>
                    </div>
                  ) : (
                    directMessages.map((msg) => (
                      <div
                        key={msg.id}
                        className={`p-3 rounded-lg max-w-[80%] ${
                          msg.sender_id === user.id
                            ? 'ml-auto bg-primary text-primary-foreground'
                            : 'bg-muted'
                        }`}
                      >
                        <p className="text-sm">{msg.content}</p>
                        <p className="text-xs opacity-70 mt-1">
                          {msg.created_at && format(new Date(msg.created_at), 'h:mm a')}
                        </p>
                      </div>
                    ))
                  )}
                </CardContent>
                {selectedCoach && (
                  <div className="border-t p-4">
                    <div className="flex gap-2">
                      <Input
                        placeholder={`Message ${selectedCoach.name}...`}
                        value={newDM}
                        onChange={(e) => setNewDM(e.target.value)}
                        onKeyPress={(e) => e.key === 'Enter' && sendDirectMessage()}
                        data-testid="dm-input"
                      />
                      <Button onClick={sendDirectMessage} disabled={!newDM.trim()} data-testid="send-dm-btn">
                        <Send className="h-4 w-4" />
                      </Button>
                    </div>
                  </div>
                )}
              </Card>
            </div>
          </TabsContent>

          {/* Email/SMS Tab */}
          <TabsContent value="email-sms">
            <Card className="border-2">
              <CardHeader className="border-b bg-muted/30 flex flex-row items-center justify-between">
                <CardTitle className="font-heading uppercase text-xl">Send Email or SMS</CardTitle>
                <Dialog open={showComposeDialog} onOpenChange={setShowComposeDialog}>
                  <DialogTrigger asChild>
                    <Button data-testid="compose-btn">
                      <Mail className="h-4 w-4 mr-2" />
                      Compose Message
                    </Button>
                  </DialogTrigger>
                  <DialogContent className="max-w-2xl">
                    <DialogHeader>
                      <DialogTitle>Compose Message</DialogTitle>
                    </DialogHeader>
                    <div className="space-y-4">
                      {/* Message Type Selection */}
                      <div className="flex gap-4">
                        <Button
                          variant={composeMode === 'email' ? 'default' : 'outline'}
                          onClick={() => setComposeMode('email')}
                          className="flex-1"
                          data-testid="mode-email"
                        >
                          <Mail className="h-4 w-4 mr-2" />
                          Email
                        </Button>
                        <Button
                          variant={composeMode === 'sms' ? 'default' : 'outline'}
                          onClick={() => setComposeMode('sms')}
                          className="flex-1"
                          data-testid="mode-sms"
                        >
                          <Phone className="h-4 w-4 mr-2" />
                          SMS
                        </Button>
                      </div>

                      {/* Email Template Picker */}
                      {composeMode === 'email' && (
                        <div className="space-y-2">
                          <Label>Template (optional)</Label>
                          <Select value={selectedTemplate} onValueChange={applyTemplate}>
                            <SelectTrigger data-testid="email-template-select">
                              <SelectValue placeholder="Start from a template..." />
                            </SelectTrigger>
                            <SelectContent>
                              {emailTemplates.map((t) => (
                                <SelectItem key={t.id} value={t.id} data-testid={`template-option-${t.id}`}>
                                  {t.name}
                                </SelectItem>
                              ))}
                            </SelectContent>
                          </Select>
                          <p className="text-xs text-muted-foreground">
                            Templates prefill the subject and message. Fill in the details below.
                          </p>
                          {selectedTemplate && (
                            <div className="space-y-2">
                              {['game_reminder', 'practice_reminder', 'schedule_change'].includes(selectedTemplate) && upcomingEvents.length > 0 && (
                                <Select value="" onValueChange={applyScheduleEvent}>
                                  <SelectTrigger data-testid="schedule-autofill-select" className="border-primary/50">
                                    <SelectValue placeholder="⚡ Autofill from upcoming schedule..." />
                                  </SelectTrigger>
                                  <SelectContent>
                                    {upcomingEvents.map((ev) => (
                                      <SelectItem key={ev.id} value={ev.id} data-testid={`schedule-event-${ev.id}`}>
                                        {ev.date} — {ev.title}{ev.time ? ` at ${ev.time}` : ''}
                                      </SelectItem>
                                    ))}
                                  </SelectContent>
                                </Select>
                              )}
                              {selectedTemplate === 'weekly_recap' && (
                                <Button
                                  type="button"
                                  variant="outline"
                                  className="w-full border-primary/50"
                                  onClick={autoGenerateRecap}
                                  data-testid="auto-generate-recap-btn"
                                >
                                  ⚡ Auto-Generate from This Week's Stats &amp; Schedule
                                </Button>
                              )}
                              <div className="grid grid-cols-2 gap-2 p-3 bg-muted/40 rounded-md border" data-testid="template-fill-form">
                              {(emailTemplates.find((t) => t.id === selectedTemplate)?.fields || []).map((field) => (
                                <div key={field} className="space-y-1">
                                  <Label className="text-xs capitalize">{field.replace(/_/g, ' ')}</Label>
                                  <Input
                                    value={templateValues[field] || ''}
                                    onChange={(e) => updateTemplateValue(field, e.target.value)}
                                    placeholder={field.replace(/_/g, ' ')}
                                    className="h-8 text-sm"
                                    data-testid={`template-field-${field}`}
                                  />
                                </div>
                              ))}
                            </div>
                            </div>
                          )}
                        </div>
                      )}

                      {/* Recipients Selection */}
                      <div className="space-y-2">
                        <div className="flex items-center justify-between">
                          <Label>Recipients ({recipients.length} selected)</Label>
                          <Button variant="link" size="sm" onClick={selectAllRecipients}>
                            Select All
                          </Button>
                        </div>
                        <div className="border rounded-lg max-h-40 overflow-y-auto p-2 space-y-1">
                          {contacts.length === 0 ? (
                            <p className="text-sm text-muted-foreground p-2">No contacts found. Add contacts first.</p>
                          ) : (
                            contacts.map((contact) => (
                              <div
                                key={contact.id}
                                className={`flex items-center gap-2 p-2 rounded cursor-pointer hover:bg-muted ${
                                  recipients.includes(contact.id) ? 'bg-primary/10' : ''
                                }`}
                                onClick={() => toggleRecipient(contact)}
                              >
                                <div className={`h-4 w-4 border rounded ${
                                  recipients.includes(contact.id) ? 'bg-primary border-primary' : ''
                                }`}>
                                  {recipients.includes(contact.id) && (
                                    <CheckCircle className="h-4 w-4 text-white" />
                                  )}
                                </div>
                                <span className="text-sm">
                                  {contact.first_name} {contact.last_name}
                                  <span className="text-muted-foreground ml-2">
                                    ({composeMode === 'email' ? contact.email : contact.phone})
                                  </span>
                                </span>
                              </div>
                            ))
                          )}
                        </div>
                      </div>

                      {/* Subject (Email only) */}
                      {composeMode === 'email' && (
                        <div className="space-y-2">
                          <Label>Subject</Label>
                          <Input
                            placeholder="Enter email subject"
                            value={emailSubject}
                            onChange={(e) => setEmailSubject(e.target.value)}
                            data-testid="email-subject"
                          />
                        </div>
                      )}

                      {/* Message Body */}
                      <div className="space-y-2">
                        <Label>Message</Label>
                        <Textarea
                          placeholder={composeMode === 'email' ? 'Enter your email message...' : 'Enter your SMS message (160 chars recommended)...'}
                          value={emailBody}
                          onChange={(e) => setEmailBody(e.target.value)}
                          rows={composeMode === 'email' ? 6 : 3}
                          data-testid="message-body"
                        />
                        {composeMode === 'sms' && (
                          <p className="text-xs text-muted-foreground">{emailBody.length}/160 characters</p>
                        )}
                      </div>

                      {/* Send Button */}
                      <Button
                        className="w-full"
                        onClick={composeMode === 'email' ? handleSendEmail : handleSendSMS}
                        disabled={isSending || !canEdit('communication')}
                        data-testid="send-message-btn"
                      >
                        {isSending ? (
                          <>
                            <RefreshCw className="h-4 w-4 mr-2 animate-spin" />
                            Sending...
                          </>
                        ) : (
                          <>
                            <Send className="h-4 w-4 mr-2" />
                            Send {composeMode === 'email' ? 'Email' : 'SMS'}
                          </>
                        )}
                      </Button>
                    </div>
                  </DialogContent>
                </Dialog>
              </CardHeader>
              <CardContent className="p-6">
                <div className="grid grid-cols-2 gap-6">
                  <Card className="bg-blue-50 border-blue-200">
                    <CardContent className="p-6 text-center">
                      <Mail className="h-12 w-12 mx-auto text-blue-600 mb-4" />
                      <h3 className="font-bold text-lg mb-2">Email</h3>
                      <p className="text-sm text-muted-foreground mb-4">
                        Send emails to parents and contacts
                      </p>
                      <Button 
                        variant="outline" 
                        onClick={() => { setComposeMode('email'); setShowComposeDialog(true); }}
                      >
                        Compose Email
                      </Button>
                    </CardContent>
                  </Card>
                  <Card className="bg-green-50 border-green-200">
                    <CardContent className="p-6 text-center">
                      <Phone className="h-12 w-12 mx-auto text-green-600 mb-4" />
                      <h3 className="font-bold text-lg mb-2">SMS</h3>
                      <p className="text-sm text-muted-foreground mb-4">
                        Send text messages for urgent updates
                      </p>
                      <Button 
                        variant="outline"
                        onClick={() => { setComposeMode('sms'); setShowComposeDialog(true); }}
                        data-testid="compose-sms-shortcut-btn"
                      >
                        Compose SMS
                      </Button>
                    </CardContent>
                  </Card>
                </div>
                {lastSmsBlast && (
                  <Card className="mt-6 border-2" data-testid="sms-delivery-dashboard">
                    <CardHeader className="border-b bg-muted/30 py-3">
                      <CardTitle className="font-heading uppercase text-sm flex items-center justify-between">
                        <span>Last SMS Blast</span>
                        <span className="text-xs text-muted-foreground font-normal normal-case">
                          {lastSmsBlast.created_at && format(new Date(lastSmsBlast.created_at), 'MMM d, h:mm a')}
                        </span>
                      </CardTitle>
                    </CardHeader>
                    <CardContent className="p-4">
                      <p className="text-sm text-muted-foreground mb-3 truncate" data-testid="sms-delivery-dashboard-message">
                        "{lastSmsBlast.message}"
                      </p>
                      <div className="grid grid-cols-3 gap-3">
                        <div className="text-center p-3 rounded-lg bg-green-50 border border-green-200" data-testid="sms-delivery-dashboard-sent">
                          <p className="text-2xl font-black text-green-700">{lastSmsBlast.sms_sent_count}</p>
                          <p className="text-xs text-muted-foreground uppercase tracking-wide">Sent</p>
                        </div>
                        <div className="text-center p-3 rounded-lg bg-red-50 border border-red-200" data-testid="sms-delivery-dashboard-failed">
                          <p className="text-2xl font-black text-red-700">{lastSmsBlast.sms_failed_count}</p>
                          <p className="text-xs text-muted-foreground uppercase tracking-wide">Failed</p>
                        </div>
                        <div className="text-center p-3 rounded-lg bg-muted border" data-testid="sms-delivery-dashboard-total">
                          <p className="text-2xl font-black">{lastSmsBlast.recipients_count}</p>
                          <p className="text-xs text-muted-foreground uppercase tracking-wide">Total</p>
                        </div>
                      </div>
                      {lastSmsBlast.sms_failed_count > 0 && (
                        <Button
                          className="w-full mt-4"
                          variant="outline"
                          disabled={isRetryingSms}
                          onClick={handleRetryFailedSms}
                          data-testid="sms-delivery-dashboard-retry-btn"
                        >
                          {isRetryingSms ? 'Retrying...' : `Retry ${lastSmsBlast.sms_failed_count} Failed`}
                        </Button>
                      )}
                    </CardContent>
                  </Card>
                )}
              </CardContent>
            </Card>
          </TabsContent>

          {/* History Tab */}
          <TabsContent value="history">
            <Card className="border-2">
              <CardHeader className="border-b bg-muted/30 flex flex-row items-center justify-between">
                <CardTitle className="font-heading uppercase text-xl">Message History</CardTitle>
                <Button variant="outline" size="sm" onClick={loadMessageLogs}>
                  <RefreshCw className="h-4 w-4 mr-2" />
                  Refresh
                </Button>
              </CardHeader>
              <CardContent className="p-0">
                <div className="divide-y">
                  {messageLogs.length === 0 ? (
                    <div className="text-center py-12">
                      <Clock className="h-16 w-16 mx-auto text-muted-foreground opacity-50" />
                      <p className="text-muted-foreground mt-4">No message history yet</p>
                    </div>
                  ) : (
                    messageLogs.map((log, index) => (
                      <div key={log.id || index} className="p-4 flex items-center gap-4 hover:bg-muted/50">
                        <div className={`p-2 rounded-full ${
                          log.type === 'email' ? 'bg-blue-100' : 'bg-green-100'
                        }`}>
                          {log.type === 'email' ? (
                            <Mail className="h-4 w-4 text-blue-600" />
                          ) : (
                            <Phone className="h-4 w-4 text-green-600" />
                          )}
                        </div>
                        <div className="flex-1">
                          <p className="font-semibold text-sm">{log.subject || log.message?.substring(0, 50) || 'Message'}</p>
                          <p className="text-xs text-muted-foreground">
                            To: {log.recipient_count || 1} recipient(s)
                          </p>
                        </div>
                        <Badge variant={log.status === 'sent' ? 'default' : log.status === 'failed' ? 'destructive' : 'secondary'}>
                          {log.status === 'sent' && <CheckCircle className="h-3 w-3 mr-1" />}
                          {log.status === 'failed' && <AlertCircle className="h-3 w-3 mr-1" />}
                          {log.status || 'sent'}
                        </Badge>
                        <span className="text-xs text-muted-foreground">
                          {log.created_at && format(new Date(log.created_at), 'MMM d, h:mm a')}
                        </span>
                      </div>
                    ))
                  )}
                </div>
              </CardContent>
            </Card>
          </TabsContent>
        </Tabs>
        </>
        )}
      </div>
    </Layout>
  );
}
